/**
 * 波形ピークデータの共通ユーティリティ（全オーディオプレビュー共有）。
 *
 * データソースは Lambda（audiowaveform --pixels-per-second 20 --split-channels）が
 * 生成し S3 配信される wavesurfer v2 互換 JSON。各チャンネルは min/max 交互配列
 * `[min0, max0, min1, max1, ...]`（[-1, 1] 正規化済み）。
 * JSON が無い旧ファイル向けに、ブラウザ側 decodeAudioData フォールバックも提供する。
 */

/** チャンネルごとの min/max 交互配列 */
export type WaveformPeaks = number[][];

/** Lambda / decode フォールバック共通のピーク密度（min/max ペア数 / 秒） */
export const PEAKS_PAIRS_PER_SECOND = 20;

/** decode フォールバックの対象とする最大ファイルサイズ（デコード後の PCM メモリを考慮） */
const MAX_DECODE_BYTES = 200 * 1024 * 1024;

/** 取得済み peaks のモジュールスコープキャッシュ（キー: メディアアイテム ID 等） */
export const peaksCache = new Map<string, WaveformPeaks>();

/**
 * 配列が min/max ペア形式（偶数長で min <= max の並び）かどうかの簡易判定。
 * 奇数長なら単一値形式。偶数長でも先頭数ペアの並びで単一値と判断できる場合がある。
 */
function looksLikeMinMaxPairs(channel: number[]): boolean {
  if (channel.length < 2 || channel.length % 2 !== 0) return false;
  const n = Math.min(4, Math.floor(channel.length / 2));
  for (let i = 0; i < n; i++) {
    const min = channel[i * 2];
    const max = channel[i * 2 + 1];
    if (typeof min !== 'number' || typeof max !== 'number' || min > max) return false;
  }
  return true;
}

/** 単一振幅形式 [v0, v1, ...] を対称の min/max ペア形式 [-|v0|,|v0|, ...] に変換する */
function singleValuesToMinMaxPairs(values: number[]): number[] {
  const out = new Array<number>(values.length * 2);
  for (let i = 0; i < values.length; i++) {
    const v = typeof values[i] === 'number' ? Math.abs(values[i]) : 0;
    out[i * 2] = -v;
    out[i * 2 + 1] = v;
  }
  return out;
}

/**
 * audiowaveform 生出力（--split-channels）のフラット配列をチャンネル別に分離する。
 * ポイント i の並びは [ch0min, ch0max, ch1min, ch1max, ...]。
 */
function deinterleaveFlatChannels(data: number[], channelCount: number): number[][] {
  const stride = channelCount * 2;
  const pointCount = Math.floor(data.length / stride);
  const channels: number[][] = [];
  for (let ch = 0; ch < channelCount; ch++) {
    const chData = new Array<number>(pointCount * 2);
    for (let i = 0; i < pointCount; i++) {
      chData[i * 2] = data[i * stride + ch * 2];
      chData[i * 2 + 1] = data[i * stride + ch * 2 + 1];
    }
    channels.push(chData);
  }
  return channels;
}

/**
 * peaks JSON（wavesurfer v2 形式ほか複数の形状に対応）をチャンネル配列に正規化する。
 * 対応形状: 生配列 / { data } / { peaks } / { waveform }。
 * Lambda 生成 JSON はマルチチャンネル時にネスト配列済みだが、
 * `channels > 1` かつフラット配列（audiowaveform 生出力）はインターリーブ解除する。
 * 単一振幅形式のチャンネルは min/max ペア形式に変換する。
 */
export function normalizePeaksJson(json: unknown): WaveformPeaks {
  let raw: unknown;
  let declaredChannels = 1;
  if (Array.isArray(json)) {
    raw = json;
  } else if (json && typeof json === 'object') {
    const obj = json as Record<string, unknown>;
    raw = obj.data ?? obj.peaks ?? obj.waveform;
    if (typeof obj.channels === 'number' && obj.channels > 1) {
      declaredChannels = Math.floor(obj.channels);
    }
  }
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error('Invalid waveform JSON');
  }

  const channels: number[][] = Array.isArray(raw[0])
    ? (raw as number[][])
    : declaredChannels > 1
      ? deinterleaveFlatChannels(raw as number[], declaredChannels)
      : [raw as number[]];

  return channels.map(ch => {
    if (!Array.isArray(ch) || ch.length === 0) return [];
    return looksLikeMinMaxPairs(ch) ? ch : singleValuesToMinMaxPairs(ch);
  });
}

/** peaks の min/max ペア数（0 チャンネル時は 0） */
export function getPeaksPairCount(peaks: WaveformPeaks): number {
  return peaks.length > 0 ? Math.floor(peaks[0].length / 2) : 0;
}

/** ピーク密度（20 pairs/秒）から総尺を推定する（メタデータ未取得時の表示用） */
export function estimatePeaksDuration(peaks: WaveformPeaks | null): number {
  if (!peaks) return 0;
  return getPeaksPairCount(peaks) / PEAKS_PAIRS_PER_SECOND;
}

/**
 * AudioBuffer からチャンネルごとの min/max 交互ピーク配列を計算する。
 * Lambda 生成 JSON（--split-channels）と同じチャンネル別形式で返し、
 * 正規化は全チャンネル共通の最大値で行う（チャンネル間の相対レベルを保持）。
 */
export function computePeaksFromAudioBuffer(buffer: AudioBuffer, pairsPerSecond: number): WaveformPeaks {
  const totalPairs = Math.max(1, Math.ceil(buffer.duration * pairsPerSecond));
  const samplesPerPair = Math.max(1, Math.floor(buffer.length / totalPairs));
  const channelCount = Math.max(1, buffer.numberOfChannels);

  const out: WaveformPeaks = [];
  let maxAbs = 0;

  for (let ch = 0; ch < channelCount; ch++) {
    const data = buffer.getChannelData(ch);
    const chOut = new Array<number>(totalPairs * 2);

    for (let i = 0; i < totalPairs; i++) {
      const start = i * samplesPerPair;
      const end = Math.min(start + samplesPerPair, buffer.length);
      let min = 0;
      let max = 0;
      for (let s = start; s < end; s++) {
        const v = data[s];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      chOut[i * 2] = min;
      chOut[i * 2 + 1] = max;
      const abs = Math.max(-min, max);
      if (abs > maxAbs) maxAbs = abs;
    }

    out.push(chOut);
  }

  // サーバー側生成と同じく [-1, 1] に正規化
  if (maxAbs > 0) {
    for (const chOut of out) {
      for (let i = 0; i < chOut.length; i++) {
        chOut[i] = Math.round((chOut[i] / maxAbs) * 100) / 100;
      }
    }
  }
  return out;
}

/** Lambda 事前計算の peaks JSON を取得して正規化する */
export async function fetchPeaksJson(url: string, signal: AbortSignal): Promise<WaveformPeaks> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Waveform fetch failed: ${res.status}`);
  return normalizePeaksJson(await res.json());
}

/** 元メディアをブラウザ側でデコードして peaks を計算する（波形 JSON 未生成の旧ファイル向け） */
export async function decodePeaksFromMedia(url: string, signal: AbortSignal): Promise<WaveformPeaks> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Media fetch failed: ${res.status}`);
  const arrayBuffer = await res.arrayBuffer();
  if (arrayBuffer.byteLength > MAX_DECODE_BYTES) {
    throw new Error('Media file too large for browser-side waveform decoding');
  }

  const AudioContextCtor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) throw new Error('AudioContext not supported');

  const ctx = new AudioContextCtor();
  try {
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    return computePeaksFromAudioBuffer(audioBuffer, PEAKS_PAIRS_PER_SECOND);
  } finally {
    ctx.close().catch(() => {});
  }
}

/** 秒数を "M:SS" 形式にフォーマットする（NaN / Infinity は "0:00"） */
export function formatAudioTime(time: number): string {
  if (!isFinite(time) || time < 0) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
